

































import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import Highcharts from 'highcharts';
import { DocumentComponentsState, DocumentRepoChartTab, DocumentRepoState } from '@/utils/types/store';
import format from '@/utils/format';
import functions from '@/utils/functions';
import { DocumentParagraph } from '@/utils/types/documentRepo/documentComponent';
import { DocumentRepoCommitDTO } from '@/utils/types/documentRepo/documentRepoCommit';
import { Readability } from '@/utils/types/common';
import DocumentRepoReadabilityChart from './DocumentRepoReadabilityChart.vue';

const { ifPresent } = functions;

export default Vue.extend({
  name: 'DocumentRepoChart',
  components: {
    DocumentRepoReadabilityChart,
  },
  data() {
    return {
      tooltipFormatter: (function formatter(): string | void {
        return ifPresent(format.formatReadabilityScore)(this.y);
      }) as Highcharts.FormatterCallbackFunction<Highcharts.Point>,
      selectedCommitIndex: 0,
      selectedParagraphIndex: undefined as number | undefined,
    };
  },
  computed: {
    ...mapState('documentRepo', {
      documentRepo: (state) => (state as DocumentRepoState).documentRepo,
      documentRepoCommits: (state) => (state as DocumentRepoState).documentRepoCommits,
    }),
    ...mapState('documentComponents', {
      documentComponentsByCommitId:
        (state) => (state as DocumentComponentsState).documentParagraphsByCommitId,
    }),
    documentRepoCommitsLoaded(): boolean {
      return this.documentRepoCommits && this.documentRepoCommits.length !== 0;
    },
    selectedCommit(): DocumentRepoCommitDTO | undefined {
      if (!this.documentRepoCommitsLoaded) {
        return undefined;
      }

      const commit = this.documentRepoCommits[this.selectedCommitIndex];

      this.loadCommitData({
        docId: this.documentRepo.id,
        commitId: commit.id,
      });

      return commit;
    },
    singleRevisionChartTitle(): string {
      const revision = this.selectedCommit
        ? this.formatRevision(this.selectedCommit.millis, this.selectedCommit.hash)
        : '---';
      return `Readability of document at revision ${revision}`;
    },
    paragraphsOfCommitToShow(): DocumentParagraph[] {
      if (!this.selectedCommit) {
        return [];
      }

      return this.documentComponentsByCommitId[this.selectedCommit.id];
    },
    loadingChartOptions(): Highcharts.Options {
      return {
        chart: { events: { load() { this.showLoading(); } } },
      };
    },
    singleRevisionChartOptions(): Highcharts.Options {
      if (!this.paragraphsOfCommitToShow || this.paragraphsOfCommitToShow.length === 0) {
        return this.loadingChartOptions;
      }
      const readabilities = this.paragraphsOfCommitToShow.map((p) => p.readability);
      return this.createReadabilitySeriesOptions(readabilities);
    },
    allRevisionsChartOptions(): Highcharts.Options {
      if (!this.documentRepoCommitsLoaded) {
        return this.loadingChartOptions;
      }
      const readabilities = this.documentRepoCommits.map((c) => c.readability);
      return this.createReadabilitySeriesOptions(readabilities);
    },
    selectedChartTab: {
      get(): DocumentRepoChartTab {
        return (this.$store.state.documentRepo as DocumentRepoState).selectedChartTab;
      },
      set(newChartTab: DocumentRepoChartTab) {
        this.selectDocumentRepoChartTab(newChartTab as DocumentRepoChartTab);
      },
    },
  },
  watch: {
    documentRepoCommits(newCommits) {
      this.selectedCommitIndex = (newCommits?.length - 1) ?? 0;
    },
    selectedCommitIndex() {
      // Clear selected paragraph index
      this.selectedParagraphIndex = undefined;
    },
  },
  methods: {
    ...mapActions('documentRepo', [
      'loadCommitData',
      'selectDocumentRepoChartTab',
    ]),
    ...mapActions('documentComponents', [
      'setSelectedComponentIndex',
    ]),
    selectParagraphIndex(index: number) {
      this.selectedParagraphIndex = index;
      this.setSelectedComponentIndex(index);
    },
    selectCommitIndex(index: number) {
      this.selectedCommitIndex = index;
    },
    formatSliderTooltip(commitIdx: number): string {
      if (this.documentRepoCommitsLoaded) {
        const { millis, hash } = this.documentRepoCommits[commitIdx];
        return this.formatRevision(millis, hash);
      }
      return '---';
    },
    createReadabilitySeriesOptions(rs: Readability[]): Highcharts.Options {
      const data = {
        fleshReadingEase: rs.map((r) => ({
          y: r.fleschReadingEase,
        })),
        fleschKincaidGradeLevel: rs.map((r) => ({
          y: r.fleschKincaidGradeLevel,
        })),
        daleChallScore: rs.map((r) => ({
          y: r.daleChallScore,
        })),
        papersDomainScore: rs.map((r) => ({
          y: r.papersDomainScore,
        })),
      };

      return {
        series: [{
          name: 'Flesch reading ease',
          data: data.fleshReadingEase,
          tooltip: { pointFormatter: this.tooltipFormatter },
        }, {
          name: 'Flesch—Kincaid grade level',
          data: data.fleschKincaidGradeLevel,
          tooltip: { pointFormatter: this.tooltipFormatter },
        }, {
          name: 'Dale—Chall score',
          data: data.daleChallScore,
          tooltip: { pointFormatter: this.tooltipFormatter },
        }, {
          name: 'Papers domain score',
          data: data.papersDomainScore,
          tooltip: { pointFormatter: this.tooltipFormatter },
        }],
      } as Highcharts.Options;
    },
    formatDate: format.formatDate,
    formatRevision: format.formatRevision,
  },
});
